<template>
  <Loading v-if="loading || !user" />
  <div class="infoUser">
    <div class="infoUser__header">
      <Link :title="$t('backToUsers')" @backTo="backTo" />
    </div>
    <Title :title="$t('user')" />
    <div class="infoUser__card">
      <FormKit
        type="form"
        id="addUserForm"
        v-model="formData"
        :actions="false"
        v-if="user"
        @submit="submitHandler"
      >
        <AboutUser
          :userRoleDropdown="userRoleDropdown"
          @selectUserRole="selectUserRole"
        />
        <FormKit type="group" name="role">
          <Checkbox
            class="infoUser__card-checkbox"
            name="webToolAccess"
            :value="formData.role.webToolAccess"
            :title="$t('webToolAccess')"
            @change="formData.role.webToolAccess = !formData.role.webToolAccess"
            data-testid="checkWebToolAccess"
          />
          <Checkbox
            class="infoUser__card-checkbox"
            name="boxAccess"
            :value="formData.role.boxAccess"
            :title="$t('boxAccess')"
            @change="formData.role.boxAccess = !formData.role.boxAccess"
            data-testid="checkBoxAccess"
          />
        </FormKit>
      </FormKit>
      <div class="infoUser__card__lastUpdate-key" v-if="user">
        <div class="content" v-if="user.updatedAt">
          <p class="title">{{ $t('lastUpdate') }}</p>
          <span class="subtitle">{{ dateTime(user?.updatedAt) }}</span>
        </div>
        <div class="content">
          <p class="title">{{ $t('key') }}</p>
          <span class="subtitle">{{ user?.id }}</span>
        </div>
      </div>
      <Button
        v-if="accountUser?.role.name === 'main_user'"
        class="button__removePharmacy"
        variant="redBordered"
        :title="$t('removeFromMyPharmacy')"
        @click="removeFromMyPharmacy"
      />
    </div>
  </div>
  <Footer @cleanForm="cleanForm" @submitForm="submitForm" />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import moment from 'moment';
import { UserProfile } from 'firebase/auth';

import router from '@/router';
import Loading from '@/views/Loading.vue';

import AboutUser from './AboutUser/AboutUser.vue';

import { columns } from './configs';

import { Option } from '@/types';
import { AppRole } from '@/store/modules/roles/types';
import { PharmacyAccesses } from '@/store/modules/account/types';

@Options({
  name: 'InfoUser',
  components: {
    Loading,
    AboutUser
  },
  data() {
    return {
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        role: {
          boxAccess: true,
          webToolAccess: true,
          name: ''
        },
        pharmacistPin: ''
      },
      userRoleDropdown: {
        selectedOption: {
          value: '',
          label: ''
        },
        options: []
      },
      columns: columns(this.$t),
      loading: false,
      showModal: false
    };
  },
  created() {
    const { id } = this.$route.params;

    if (id) {
      this.fetchUserAndRoles();
    }
  },
  unmounted() {
    this.clearUser();
  },
  computed: {
    ...mapState('account', {
      // eslint-disable-next-line
      accountUser: (state: any) => state.user
    }),
    ...mapState('users', ['user']),
    ...mapState('roles', ['appRoles'])
  },
  methods: {
    ...mapActions('roles', ['getAppRoles']),
    ...mapActions('users', [
      'getUser',
      'clearUser',
      'updateUser',
      'removePharmacyAccess'
    ]),
    async fetchUserAndRoles() {
      await this.getAppRoles();
      await this.fetchUser();
      await this.initUserRole();
    },
    async fetchUser() {
      const { id } = this.$route.params;

      this.loading = true;
      await this.getUser(id);

      const role = { ...this.user.role };
      delete role.permissions;
      delete role.app_access;
      delete role.level;

      this.formData = {
        ...this.user,
        role
      };

      this.loading = false;
    },
    initUserRole() {
      this.userRoleDropdown.options = this.appRoles.map((i: AppRole) => ({
        value: i.value,
        label: i.label
      }));

      if (this.user.pharmacyAccesses) {
        const id = this.user.pharmacyId;
        const pharmacyId = localStorage.getItem('id');

        const roleNameForPharmacy: PharmacyAccesses | undefined =
          this.user.pharmacyAccesses.find(
            (itemData: PharmacyAccesses) => itemData.pharmacyId === id
          );

        const roleNameForPharmacyId: PharmacyAccesses | undefined =
          this.user.pharmacyAccesses.find(
            (itemData: PharmacyAccesses) => itemData.pharmacyId === pharmacyId
          );

        if (roleNameForPharmacyId) {
          this.userRoleDropdown.selectedOption =
            this.userRoleDropdown.options.find(
              (i: Option) => i.value === roleNameForPharmacyId.roleName
            );
        } else if (roleNameForPharmacy) {
          this.userRoleDropdown.selectedOption =
            this.userRoleDropdown.options.find(
              (i: Option) => i.value === roleNameForPharmacy.roleName
            );
        } else {
          this.userRoleDropdown.selectedOption =
            this.userRoleDropdown.options.find(
              (i: Option) => i.value === this.user.role.name
            );
        }
      } else {
        this.userRoleDropdown.selectedOption =
          this.userRoleDropdown.options.find(
            (i: Option) => i.value === this.user.role.name
          );
      }
    },
    backTo() {
      router.push({ name: 'Users' });
    },
    selectUserRole(option: Option) {
      this.userRoleDropdown.selectedOption = option;
      this.formData.role.name = option.value;
    },
    async submitHandler(data: UserProfile) {
      const payload = {
        ...data,
        role: this.formData.role.name,
        webToolAccess: this.formData.role.webToolAccess,
        boxAccess: this.formData.role.boxAccess
      };

      const { id } = this.$route.params;

      this.loading = true;

      await this.updateUser({
        data: payload,
        userId: id
      });

      router.push({ name: 'Users' });
      this.loading = false;
    },
    async removeFromMyPharmacy() {
      this.loading = true;

      await this.removePharmacyAccess(this.user.id);
      router.push({ name: 'Users' });

      this.loading = false;
    },
    cleanForm() {
      this.$formkit.reset('addUserForm');
    },
    submitForm() {
      this.$formkit.submit('addUserForm');
    },
    dateTime(value: string) {
      return moment(value).format('DD.MM.YYYY HH:mm:ss');
    }
  }
})
export default class InfoUserComponent extends Vue {}
</script>

<style lang="scss" scoped>
.infoUser {
  height: calc(100vh - 80px);
  padding: 20px;
  overflow-y: scroll;
  overflow-x: hidden;
  .infoUser__header {
    display: flex;
    justify-content: space-between;
    align-content: center;
    margin-bottom: 27px;
    .infoUser__header-changePassword {
      display: flex;
      align-items: center;
      margin-right: 30px;
      cursor: pointer;
      .title {
        margin-left: 9px;
        color: $main-green;
        font-size: $fourteenSize;
        line-height: $fourteenSize;
        font-weight: 400;
      }
    }
  }
  .infoUser__card {
    width: 100%;
    .infoUser__card-checkbox {
      margin-top: 0;
      &:last-child {
        margin-top: 24px;
      }
    }
    .infoUser__card__lastUpdate-key {
      margin-top: 32px;
      .content {
        &:last-child {
          margin-top: 32px;
          margin-bottom: 32px;
        }
        .title {
          font-size: $fourteenSize;
          line-height: 14px;
          color: $label;
          padding-bottom: 12px;
        }
        .subtitle {
          font-family: 'Ubuntu-Medium';
          font-size: $sixteenSize;
          line-height: 14px;
        }
      }
    }
    .button__removePharmacy {
      width: 230px;
    }
  }
}
</style>
